import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Calendar, User, Clock } from 'lucide-react';
import { calendarService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { CalendarSlot, SlotCreationParams } from '../types/calendar';
import PageHeader from '../components/ui/PageHeader';
import CalendarView from '../components/calendar/CalendarView';
import DaySlotManager from '../components/calendar/DaySlotManager';
import { Button } from '../components/ui';
import { formatDate, formatDateTime } from '../lib/formatters';

function CalendarSchedule() {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  // Set today as the default selected date
  useEffect(() => {
    if (!selectedDate) {
      // Use YYYY-MM-DD format without timezone conversion
      setSelectedDate(formatDate(new Date(), 'iso'));
    }
  }, [selectedDate]);

  // Queries
  const { data: professionals, isLoading: isLoadingProfessionals } = useQuery({
    queryKey: ['calendar-professionals'],
    queryFn: () => calendarService.getProfessionals(),
  });

  const { data: serviceTypes, isLoading: isLoadingServiceTypes } = useQuery({
    queryKey: ['calendar-service-types'],
    queryFn: () => calendarService.getServiceTypes(),
  });

  // Get slots for the current month
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  // Format dates as YYYY-MM-DD without timezone conversion
  const startDate = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`;
  const endDate = `${currentYear}-${String(currentMonth + 2).padStart(2, '0')}-01`;

  const { data: slots, isLoading: isLoadingSlots } = useQuery({
    queryKey: ['calendar-slots', startDate, endDate, selectedProfessionalId],
    queryFn: () =>
      calendarService.getSlots({
        date_start: startDate,
        date_end: endDate,
        professional_id: selectedProfessionalId || undefined,
      }),
  });

  // Get slots for the selected date
  const { data: daySlots, isLoading: isLoadingDaySlots } = useQuery({
    queryKey: ['calendar-day-slots', selectedDate, selectedProfessionalId],
    queryFn: () =>
      calendarService.getSlots({
        date_start: selectedDate || undefined,
        date_end: selectedDate || undefined,
        professional_id: selectedProfessionalId || undefined,
      }),
    enabled: !!selectedDate,
  });

  // Mutations
  const createSlotMutation = useMutation({
    mutationFn: (data: Partial<CalendarSlot>) => calendarService.createSlot(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-slots'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-day-slots'] });
      messageHandler.success('Horário criado com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const createMultipleSlotsMutation = useMutation({
    mutationFn: (params: SlotCreationParams) => calendarService.createMultipleSlots(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-slots'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-day-slots'] });
      messageHandler.success('Horários criados com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteSlotMutation = useMutation({
    mutationFn: (id: number) => calendarService.deleteSlot(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-slots'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-day-slots'] });
      messageHandler.success('Horário excluído com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  // Handlers
  const handleDateClick = (date: string) => {
    // Ensure date is in YYYY-MM-DD format without timezone conversion
    setSelectedDate(formatDate(date, 'iso'));
  };

  const handleProfessionalChange = (professionalId: number) => {
    setSelectedProfessionalId(professionalId || null);
  };

  const handleCreateSlot = async (data: Partial<CalendarSlot>) => {
    await createSlotMutation.mutateAsync(data);
  };

  const handleCreateMultipleSlots = async (params: SlotCreationParams) => {
    await createMultipleSlotsMutation.mutateAsync(params);
  };

  const handleDeleteSlot = async (id: number) => {
    await deleteSlotMutation.mutateAsync(id);
  };

  const isLoading = isLoadingProfessionals || isLoadingServiceTypes || isLoadingSlots;

  // Render day content for the popup
  const renderDayContent = (date: string) => {
    return (
      <DaySlotManager
        date={date}
        professionals={professionals || []}
        serviceTypes={serviceTypes || []}
        slots={daySlots || []}
        selectedProfessionalId={selectedProfessionalId}
        onCreateSlot={handleCreateSlot}
        onCreateMultipleSlots={handleCreateMultipleSlots}
        onDeleteSlot={handleDeleteSlot}
        isLoading={
          createSlotMutation.isPending || deleteSlotMutation.isPending || isLoadingDaySlots
        }
      />
    );
  };

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Agenda"
          description="Gerencie os horários disponíveis para agendamento."
          actions={
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => (window.location.href = '/calendar/professionals')}
              >
                <User className="h-4 w-4 mr-2" />
                Profissionais
              </Button>
              <Button
                variant="outline"
                onClick={() => (window.location.href = '/calendar/service-types')}
              >
                <Clock className="h-4 w-4 mr-2" />
                Tipos de Serviço
              </Button>
            </div>
          }
        />

        <div className="mt-6 space-y-6">
          <CalendarView
            professionals={professionals || []}
            serviceTypes={serviceTypes || []}
            slots={slots || []}
            isLoading={isLoading}
            onDateClick={handleDateClick}
            onProfessionalChange={handleProfessionalChange}
            selectedProfessionalId={selectedProfessionalId}
            selectedDate={selectedDate}
            renderDayContent={renderDayContent}
          />
        </div>
      </div>
    </div>
  );
}

export default CalendarSchedule;
